/**
 * External feed module
 * Renders the latest Eulerpool News Agency stories and AlleAktien analyses
 * from the aa-feeds Worker (https://aa-feeds.alleaktien.workers.dev) as
 * outbound teasers on the homepage.
 */

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text ?? "";
  return div.innerHTML;
}

function safeUrl(url) {
  return /^https:\/\//i.test(url || "") ? escapeHtml(url) : "";
}

function renderItem(item, config) {
  const title = escapeHtml(item.title);
  const link = safeUrl(item.link);
  if (!title || !link) return "";

  const image = safeUrl(item.image) || safeUrl(config.fallbackImage);
  const excerpt = escapeHtml(item.excerpt);
  const category = escapeHtml(item.category || config.categoryLabel || "");

  const imageHtml = image
    ? `<picture class="article-teaser__picture">
        <img src="${image}" alt="" class="article-teaser__image" loading="lazy" decoding="async" width="800" height="450">
      </picture>`
    : "";

  return `
    <article class="article-teaser${image ? "" : " article-teaser--text"}">
      <a href="${link}" class="article-teaser__link" target="_blank" rel="noopener">
        ${imageHtml}
        <div class="article-teaser__content">
          ${category ? `<span class="article-teaser__category">${category}</span>` : ""}
          <h3 class="article-teaser__title">${title}</h3>
          ${excerpt ? `<p class="article-teaser__excerpt">${excerpt}</p>` : ""}
        </div>
      </a>
    </article>`;
}

function render(items, config) {
  const container = document.getElementById(config.containerId);
  if (!container || !items?.length) return false;
  container.innerHTML = items.slice(0, config.articleCount).map((item) => renderItem(item, config)).join("");
  return true;
}

function readCache(config) {
  try {
    const stamp = Number(localStorage.getItem(config.cacheTimeKey));
    if (!stamp || Date.now() - stamp > CACHE_DURATION) return null;
    return JSON.parse(localStorage.getItem(config.cacheKey) || "null");
  } catch {
    return null;
  }
}

function writeCache(config, items) {
  try {
    localStorage.setItem(config.cacheKey, JSON.stringify(items));
    localStorage.setItem(config.cacheTimeKey, String(Date.now()));
  } catch {
    // Storage full or disabled: fine, we rendered already.
  }
}

async function load(config) {
  if (render(readCache(config), config)) return;
  try {
    const response = await fetch(`${config.feedUrl}?limit=${config.articleCount}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    const items = Array.isArray(data.items) ? data.items : [];
    if (render(items, config)) writeCache(config, items);
  } catch (error) {
    console.warn(`Feed ${config.containerId} unavailable:`, error.message);
  }
}

/**
 * @param {Object} config
 * @param {string} config.feedUrl       Worker endpoint returning { items: [...] }
 * @param {string} config.containerId   Element to render into
 * @param {string} [config.categoryLabel]
 * @param {string} config.cacheKey
 * @param {string} config.cacheTimeKey
 * @param {number} config.articleCount
 * @param {string} [config.fallbackImage]
 */
export function initRssFeed(config) {
  if (document.getElementById(config.containerId)) load(config);
}
